import HappyLine from 'js/components/HappyLine/HappyLine'
import PropTypes from 'prop-types'
import React from 'react'
import { Link } from 'react-router-dom'
import { Link as HeadLink, Meta, Title } from 'react-head'
import { withRouter } from 'react-router-dom'
import sanitize from 'sanitize'
import DefaultLayout from '../../components/DefaultLayout'
import Footer from '../../components/Footer/Footer'
import SubPageHeader from '../../components/SubPageHeader'
import { GetPaginationPage, GetCleanedPaths } from '../../utils'
import {
  getBlogPostsByPage,
  getTags,
  getBlogCategories,
  getAuthor,
} from '../../wpapi'
import Excerpt from './_BlogExcerpt'
import Tags from '../../components/Tags'
import BlogFooter from './_BlogFooter'
import SocialMediaLinks from '../../components/SocialMediaLinks'
import ErrorPage from '../../pages/ErrorPage'
import Header from '../../components/Header/Header'
import withSSR from 'js/components/withSSR/withSSR'

const TitleAndMeta = () => (
  <>
    <Title>Blog - Lifesum</Title>
    <Meta
      name="description"
      content={
        'All about healthy living. Tips on what to cook, which exercises to try, well-researched health articles and more!'
      }
    />
  </>
)

function BlogStartPageContainer({ data, isLoading, error, match, ...rest }) {
  if (isLoading) {
    return (
      <DefaultLayout>
        <TitleAndMeta />
        <SubPageHeader loading={true} magenta={true} blog={true} />
      </DefaultLayout>
    )
  }

  if (error || data.code === 'rest_post_invalid_page_number') {
    return <ErrorPage />
  }

  return (
    <BlogStartPage
      posts={data}
      pageNumber={parseInt(match.params.slug, 10) || 1}
      {...rest}
    ></BlogStartPage>
  )
}

BlogStartPageContainer.propTypes = {
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  match: PropTypes.object,
}

class BlogStartPage extends React.Component {
  jumbotronImageElement = React.createRef()

  static propTypes = {
    history: PropTypes.object.isRequired,
    loading: PropTypes.bool,
    posts: PropTypes.object,
    pageNumber: PropTypes.number,
  }

  constructor(props) {
    super(props)

    this.state = {
      posts: this.props.posts,
      tags: null,
      nextPageAvailable: false,
      latestPostsImage: false,
    }

    this.changePage = this.changePage.bind(this)
    this.getNewPosts = this.getNewPosts.bind(this)
    this.checkIfThereIsANextPage = this.checkIfThereIsANextPage.bind(this)
    this.getLatestPostsData = this.getLatestPostsData.bind(this)
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.changePage)
  }

  componentDidMount() {
    window.addEventListener('popstate', this.changePage)

    this.setState({
      posts: this.props.posts,
    })

    getTags().then(tags => {
      this.setState({
        tags: tags,
      })
    })

    this.checkIfThereIsANextPage()
  }

  changePage(page) {
    let pageToUse = page

    if (typeof page !== 'number') {
      pageToUse =
        document.location.href.indexOf('/page/') === -1
          ? 1
          : GetPaginationPage()
    }

    getBlogPostsByPage(pageToUse).then(posts => {
      this.setState(
        {
          page: pageToUse,
          posts: posts,
        },
        () => {
          if (this.props.pageNumber === 1) {
            this.getLatestPostsData()
          }
          this.checkIfThereIsANextPage()
        }
      )
    })

    window.scrollTo(0, 0)
  }

  getNewPosts(e) {
    const self = this
    e.preventDefault()
    const page = parseInt(e.target.getAttribute('data-value'), 10)
    self.changePage(page)
    if (page === 1) {
      this.props.history.push('/blog/')
    } else {
      this.props.history.push('/blog/page/' + page + '/')
    }
  }

  checkIfThereIsANextPage() {
    setTimeout(() => {
      getBlogPostsByPage(this.props.pageNumber + 1).then(data => {
        this.setState(
          {
            nextPageAvailable: data.code ? false : true,
          },
          () => {
            document.dispatchEvent(new CustomEvent('pageReady'))
          }
        )
      })
    }, 1000)
  }

  getLatestPostsData() {
    const image = this.props.posts[0]._embedded['wp:featuredmedia']
    if (image) {
      this.setState(
        {
          latestPostsImage: image,
        },
        () => {
          if (this.jumbotronImageElement.current) {
            this.jumbotronImageElement.current.addEventListener('load', () => {
              this.setState({
                latestPostsImageLoaded: true,
              })
            })
          }
        }
      )
    }

    getBlogCategories().then(categories => {
      categories.map(category => {
        if (category.id === this.props.posts[0].categories[0]) {
          this.setState({
            latestPostsCategory: category.name,
          })
        }
      })
    })

    getAuthor(this.props.posts[0].author).then(author => {
      this.setState({
        latestPostsAuthor: author,
      })
    })
  }

  get jumbotronImage() {
    if (this.props.posts[0]._embedded['wp:featuredmedia']) {
      return this.props.posts[0]._embedded['wp:featuredmedia'][0]
    }
    return null
  }

  render = () => {
    const reducedTerms = this.props.posts[0]['_embedded']['wp:term'].reduce(
      (acc, current) => {
        if (current[0]) return { ...acc, [current[0].taxonomy]: current }
        return acc
      },
      {}
    )
    const latestCategory = reducedTerms.category[0]
    return (
      <DefaultLayout>
        <TitleAndMeta />
        {this.props.pageNumber === 1 && (
          <HeadLink
            rel="canonical"
            href={
              'https://lifesum.com/' +
              GetCleanedPaths(true, '/blog').join('/') +
              '/'
            }
          />
        )}
        {this.props.pageNumber === 1 && (
          <div className="c-jumbotron c-jumbotron--message-bottom">
            {this.jumbotronImage && (
              <div className="c-jumbotron__picture">
                <img
                  className={'c-jumbotron__image'}
                  src={this.jumbotronImage.source_url}
                  alt={this.jumbotronImage.alt_text}
                  ref={this.jumbotronImageElement}
                />
              </div>
            )}
            <Header
              absolute={true}
              language={false}
              loading={this.props.loading}
              onTopOfMagenta={true}
              blog={true}
            />
            <div className="c-jumbotron__message">
              <div className="c-jumbotron__message-inner o-width-limiter">
                <h1 className="visually-hidden">Lifesum Blog</h1>
                <h2 className="c-jumbotron__heading">
                  <Link
                    to={'/blog/' + this.props.posts[0].slug}
                    className="c-jumbotron__heading-link"
                  >
                    <span className="c-jumbotron__heading-pre">
                      Fresh post:
                    </span>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: sanitize(
                          this.props.posts[0].title.rendered,
                          {}
                        ),
                      }}
                    />
                  </Link>
                </h2>
                <p className="c-jumbotron__meta">
                  {new Date(this.props.posts[0].date).toDateString()} in{' '}
                  <b className="s-text">{latestCategory.name}</b> by{' '}
                  <a
                    className="c-jumbotron__meta-link"
                    href={
                      '/blog/author/' +
                      this.props.posts[0]._embedded.author[0].slug
                    }
                  >
                    {this.props.posts[0]._embedded.author[0].name}
                  </a>
                </p>
              </div>
            </div>
            <HappyLine className="c-jumbotron__curve" color="beige-lightest" />
          </div>
        )}
        {this.props.pageNumber !== 1 && (
          <SubPageHeader heading="Lifesum Blog" magenta={true} blog={true} />
        )}
        <div className="c-content-area  o-width-limiter">
          <div className="o-grid o-grid--airy o-grid--center">
            {this.props.pageNumber === 1 &&
              this.state.posts.map((post, index) => {
                // We add these in the next `if`
                if (index === 4 || index === 8) {
                  return false
                }

                if (index === 3) {
                  return (
                    <div className="o-grid__column" data-size="24" key={index}>
                      <div className="o-grid o-grid--airy o-grid--center">
                        {this.state.tags && (
                          <div className="o-grid__column" data-size="24">
                            <Tags
                              cloud={true}
                              tags={this.state.tags}
                              heading="Popular topics"
                            />
                          </div>
                        )}
                        <div className="o-grid__column" data-size="sm:24 md:12">
                          <Excerpt post={post} />
                        </div>
                        <div className="o-grid__column" data-size="sm:24 md:12">
                          <Excerpt post={this.props.posts[4]} />
                        </div>
                      </div>
                    </div>
                  )
                }

                if (index === 7) {
                  return (
                    <div className="o-grid__column" data-size="24" key={index}>
                      <div className="o-grid o-grid--airy o-grid--center">
                        {this.state.tags && (
                          <div className="o-grid__column" data-size="24">
                            <SocialMediaLinks inList={true} />
                          </div>
                        )}
                        <div className="o-grid__column" data-size="sm:24 md:12">
                          <Excerpt post={post} />
                        </div>
                        <div className="o-grid__column" data-size="sm:24 md:12">
                          <Excerpt post={this.props.posts[8]} />
                        </div>
                      </div>
                    </div>
                  )
                }

                if (index !== 0) {
                  return (
                    <div
                      className="o-grid__column"
                      data-size="sm:24 md:12"
                      key={index}
                    >
                      <Excerpt post={post} />
                    </div>
                  )
                }
              })}
            {this.props.pageNumber !== 1 &&
              this.state.posts.map((post, index) => {
                return (
                  <div
                    className="o-grid__column"
                    data-size="sm:24 md:12"
                    key={index}
                  >
                    <Excerpt post={post} />
                  </div>
                )
              })}
          </div>
          <div className="c-paging  s-text">
            {this.props.pageNumber !== 1 && (
              <a
                className="c-paging__prev"
                href={`/blog/page/${this.props.pageNumber - 1}/`}
                onClick={this.getNewPosts}
                data-value={this.props.pageNumber - 1}
                rel="prev"
              >
                Previous page
              </a>
            )}
            {this.state.nextPageAvailable && (
              <a
                className="c-paging__next"
                href={`/blog/page/${this.props.pageNumber + 1}/`}
                onClick={this.getNewPosts}
                data-value={this.props.pageNumber + 1}
                rel="next"
              >
                Next page
              </a>
            )}
          </div>
        </div>
        <BlogFooter />
        <Footer />
      </DefaultLayout>
    )
  }
}

export default withRouter(withSSR(BlogStartPageContainer))
